// Entry point. One /ui/session call decides between the login view and the app
// shell; the language strings are fetched once per login; the router then owns
// /login, / and /tabs/<slug>.
import { loadBackend, backend, needsTenant } from './core/backend.js';
import { renderBackendPicker, renderTenantPrompt } from './core/backend-picker.js';
import { fetchSession, session, isAuthenticated } from './core/session.js';
import { loadLanguage } from './core/i18n.js';
import * as router from './core/router.js';
import { renderShell, isRendered } from './core/shell.js';
import { renderLogin } from './core/login.js';
import * as tabs from './core/tabs.js';
import { escapeHtml } from './core/tpl.js';

async function refreshSession() {
  const sess = await fetchSession();
  await loadLanguage(sess.lang, { mcelocale: sess.mcelocale });
  return sess;
}

function ensureShell() {
  if (!isRendered()) {
    // renderShell runs the legacy login sequence, which ends by showing the tab
    // for the current route (tabs.installLegacyHooks -> WB.enableNavTabs)
    renderShell(session.data);
    return false;
  }
  return true;
}

async function onLoggedIn() {
  await refreshSession();
  // a fresh document: the legacy modules keep state on WB, start clean
  window.location.replace('/');
}

function installRoutes() {
  router.route('/login', () => {
    if (isAuthenticated()) return router.navigate('/', { replace: true });
    renderLogin(session.data, onLoggedIn);
  });
  router.route('/index.html', () => router.navigate('/', { replace: true }));
  router.route('/', () => {
    if (!isAuthenticated()) return router.navigate('/login', { replace: true });
    if (ensureShell()) {
      const d = tabs.defaultTab();
      if (d) router.navigate(tabs.pathFor(d), { replace: true });
    }
  });
  router.route(/^\/tabs\/([^/]+)$/, ({ params }) => {
    if (!isAuthenticated()) return router.navigate('/login', { replace: true });
    if (ensureShell()) tabs.showSlug(params[0]);
  });
  router.otherwise(() => router.navigate('/', { replace: true }));
}

async function boot() {
  await loadBackend();
  if (needsTenant()) {
    renderTenantPrompt(document.getElementById('app'));
    return;
  }
  try {
    await refreshSession();
  } catch (err) {
    const app = document.getElementById('app');
    const where = backend.origin || window.location.origin;
    app.innerHTML =
      '<div class="container"><div class="alert alert-danger" style="margin-top:2em">' +
      'Cannot reach the Cellbie server at ' + escapeHtml(where) + ' (' + escapeHtml(err.message || String(err)) + ').</div></div>';
    renderBackendPicker(app);
    return;
  }
  installRoutes();
  router.start();
}

boot();
